import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import theme from '../../styles/theme';

const FiltrosContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: 768px) {
    padding: ${theme.spacing[4]};
    margin-bottom: ${theme.spacing[4]};
  }
`;

const FiltrosHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.$isExpanded ? theme.spacing[10] : '0'};
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing[3]};
    margin-bottom: ${props => props.$isExpanded ? theme.spacing[8] : '0'};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex: 1;
`;

const AccordionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  margin-left: auto;

  @media (min-width: 769px) {
    display: none;
  }
`;

const FiltrosContent = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease;
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  margin-bottom: ${props => props.$isExpanded ? '0' : '0'};

  @media (min-width: 769px) {
    max-height: 2000px !important;
    opacity: 1 !important;
    margin-bottom: 0 !important;
  }
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.base};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    min-height: 40px;
  }
`;

const FiltrosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[3]};
    margin-top: 0;
  }
`;

const FiltroGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Previne zoom no iOS */
    min-height: 44px;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Previne zoom no iOS */
    min-height: 44px;
  }
`;

const VeiculosFiltros = ({ filtros, onFiltrosChange, onBuscar, loading, showClienteFilter = false, clientes = [] }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isExpanded, setIsExpanded] = useState(false); // Fechado por padrão em mobile

  const uniqueClienteIds = Array.from(new Set((clientes || []).map(c => c?.id).filter(id => id != null)));
  const clienteUnicoId = uniqueClienteIds.length === 1 ? uniqueClienteIds[0].toString() : '';

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Se mudar para desktop, sempre expandir
      if (!mobile) {
        setIsExpanded(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (field, value) => {
    const novosFiltros = {
      ...filtros,
      [field]: value
    };
    
    onFiltrosChange(novosFiltros);
    
    // Se for ordenação, executar busca automaticamente
    if (field === 'ordenacao') {
      if (onBuscar) {
        onBuscar(novosFiltros);
      }
    }
  };

  const limparFiltros = () => {
    // Remover ordenação do localStorage ao limpar filtros
    try {
      localStorage.removeItem('veiculos_ordenacao');
    } catch (error) {
      // Silenciar erro ao remover do localStorage
    }

    const filtrosLimpos = {
      search: '',
      ano_min: '',
      ano_max: '',
      valor_min: '',
      valor_max: '',
      em_estoque: '',
      id_cliente: clienteUnicoId, // Se houver cliente único, mantê-lo aplicado
      ordenacao: '',
      page: 1, // Reset para primeira página
      limit: filtros.limit || 10
    };
    onFiltrosChange(filtrosLimpos);
    // Executar busca automaticamente após limpar filtros, passando os filtros limpos diretamente
    if (onBuscar) {
      onBuscar(filtrosLimpos);
    }
  };

  // Verificar se há filtros ativos (exceto id_cliente quando é cliente único)
  const filtrosParaVerificar = { ...filtros };
  if (uniqueClienteIds.length === 1) {
    delete filtrosParaVerificar.id_cliente;
  }
  const temFiltrosAtivos = Object.values(filtrosParaVerificar).some(value => value !== '' && value !== 1 && value !== 10);

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <FiltrosContainer>
      <FiltrosHeader 
        $isExpanded={isExpanded}
        onClick={isMobile ? toggleAccordion : undefined}
        style={isMobile ? { cursor: 'pointer' } : {}}
      >
        <HeaderLeft>
          <Title>
            <FiFilter />
            Filtros de Busca
          </Title>
        </HeaderLeft>
        {isMobile && (
          <AccordionButton onClick={(e) => {
            e.stopPropagation();
            toggleAccordion();
          }}>
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </AccordionButton>
        )}
        {!isMobile && (
          <Actions>
            <ActionButton onClick={limparFiltros} disabled={!temFiltrosAtivos}>
              <FiX />
              Limpar Filtros
            </ActionButton>
            <ActionButton onClick={onBuscar} disabled={loading}>
              <FiSearch />
              {loading ? 'Buscando...' : 'Buscar'}
            </ActionButton>
          </Actions>
        )}
      </FiltrosHeader>

      <FiltrosContent $isExpanded={isExpanded || !isMobile}>
        {isMobile && (
          <Actions style={{ marginBottom: theme.spacing[8], display: 'flex', gap: theme.spacing[2] }}>
            <ActionButton onClick={(e) => {
              e.stopPropagation();
              limparFiltros();
            }} disabled={!temFiltrosAtivos}>
              <FiX />
              Limpar Filtros
            </ActionButton>
            <ActionButton onClick={(e) => {
              e.stopPropagation();
              onBuscar();
            }} disabled={loading}>
              <FiSearch />
              {loading ? 'Buscando...' : 'Buscar'}
            </ActionButton>
          </Actions>
        )}

        <FiltrosGrid>
        {/* Busca geral */}
        <FiltroGroup>
          <Label>Busca Geral</Label>
          <Input
            type="text"
            placeholder="Marca, modelo, placa, code, UUID..."
            value={filtros.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
        </FiltroGroup>

        {/* Ano mínimo */}
        <FiltroGroup>
          <Label>Ano Mínimo</Label>
          <Input
            type="number"
            placeholder="2020"
            min="1990"
            max="2030"
            value={filtros.ano_min}
            onChange={(e) => handleInputChange('ano_min', e.target.value)}
          />
        </FiltroGroup>

        {/* Ano máximo */}
        <FiltroGroup>
          <Label>Ano Máximo</Label>
          <Input
            type="number"
            placeholder="2024"
            min="1990"
            max="2030"
            value={filtros.ano_max}
            onChange={(e) => handleInputChange('ano_max', e.target.value)}
          />
        </FiltroGroup>

        {/* Valor mínimo */}
        <FiltroGroup>
          <Label>Valor Mínimo (R$)</Label>
          <Input
            type="number"
            placeholder="30000"
            min="0"
            step="1000"
            value={filtros.valor_min}
            onChange={(e) => handleInputChange('valor_min', e.target.value)}
          />
        </FiltroGroup>

        {/* Valor máximo */}
        <FiltroGroup>
          <Label>Valor Máximo (R$)</Label>
          <Input
            type="number"
            placeholder="100000"
            min="0"
            step="1000"
            value={filtros.valor_max}
            onChange={(e) => handleInputChange('valor_max', e.target.value)}
          />
        </FiltroGroup>

        {/* Em estoque */}
        <FiltroGroup>
          <Label>Status</Label>
          <Select
            value={filtros.em_estoque}
            onChange={(e) => handleInputChange('em_estoque', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="true">Em Estoque</option>
            <option value="false">Vendidos</option>
          </Select>
        </FiltroGroup>

        {/* Ordenação */}
        <FiltroGroup>
          <Label>Ordenar por</Label>
          <Select
            value={filtros.ordenacao || ''}
            onChange={(e) => handleInputChange('ordenacao', e.target.value)}
          >
            <option value="">Mais recentes (padrão)</option>
            <option value="marca_modelo_asc">Marca/Modelo (A/Z) ↑</option>
            <option value="marca_modelo_desc">Marca/Modelo (Z/A) ↓</option>
            <option value="valor_asc">Valor (Crescente) ↑</option>
            <option value="valor_desc">Valor (Decrescente) ↓</option>
            <option value="ano_asc">Ano (Crescente) ↑</option>
            <option value="ano_desc">Ano (Decrescente) ↓</option>
          </Select>
        </FiltroGroup>

        {/* Filtro por cliente - mostrar apenas se showClienteFilter for true */}
        {showClienteFilter && (
          <FiltroGroup>
            <Label>Cliente</Label>
            <Select
              value={filtros.id_cliente}
              onChange={(e) => handleInputChange('id_cliente', e.target.value)}
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome_fantasia || cliente.nome_social}
                </option>
              ))}
            </Select>
          </FiltroGroup>
        )}
        </FiltrosGrid>
      </FiltrosContent>
    </FiltrosContainer>
  );
};

export default VeiculosFiltros;
